import { useContext, useEffect, useState, useRef } from "react";
import { AuthContext } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, RefreshCw } from "lucide-react";

import { FormCard } from "@/components/forms/FormCard";
import { FormEditorDialog } from "@/components/forms/FormEditorDialog";

const blankForm = () => ({
  name: "New Form",
  form_title: "Free Digital Audit",
  form_description: "Get a comprehensive audit of your online presence",
  company_name: "",
  booking_url: "",
  primary_color: "#4e3c97",
  theme: "light",
  layout: "centered",
  logo_url: "",
  background_color: "",
  form_width: 520,
  card_style: "card",
  active: true,
  routing_mode: "specific",
  assigned_user_id: null,
  pool_user_ids: [],
  geo_rules: [],
  geo_fallback_user_id: null,
});

export default function FormsManager() {
  const { API } = useContext(AuthContext);
  const [forms, setForms] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null); // open editor when not null
  const [editingId, setEditingId] = useState(null);
  const previewRef = useRef(null);

  const postPreview = () => {
    const win = previewRef.current?.contentWindow;
    if (win && draft) win.postMessage({ type: "auditpro-preview-config", config: { ...draft } }, "*");
  };

  // Push draft to the live preview iframe whenever it changes (and when it announces readiness)
  useEffect(() => {
    postPreview();
    const onMsg = (e) => { if (e.data && e.data.type === "auditpro-preview-ready") postPreview(); };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const [fRes, uRes] = await Promise.all([
        fetch(`${API}/forms`, { credentials: "include" }),
        fetch(`${API}/users`, { credentials: "include" }),
      ]);
      if (fRes.ok) setForms((await fRes.json()).forms || []);
      if (uRes.ok) setTeam((await uRes.json()).users || []);
    } catch { toast.error("Failed to load forms"); }
    finally { setLoading(false); }
  };

  const teamName = (id) => team.find((t) => t.user_id === id)?.name || team.find((t) => t.user_id === id)?.email || "—";

  const openNew = () => { setDraft(blankForm()); setEditingId(null); };
  const openEdit = (f) => {
    setDraft({
      name: f.name, form_title: f.form_title, form_description: f.form_description,
      company_name: f.company_name, booking_url: f.booking_url, primary_color: f.primary_color,
      theme: f.theme || "light", layout: f.layout || "centered", logo_url: f.logo_url || "",
      background_color: f.background_color || "", form_width: f.form_width || 520,
      card_style: f.card_style || "card",
      active: f.active, routing_mode: f.routing_mode || "specific",
      assigned_user_id: f.assigned_user_id || null, pool_user_ids: f.pool_user_ids || [],
      geo_rules: f.geo_rules || [], geo_fallback_user_id: f.geo_fallback_user_id || null,
    });
    setEditingId(f.form_id);
  };

  const closeEditor = () => { setDraft(null); setEditingId(null); };

  const save = async () => {
    setSaving(true);
    try {
      const url = editingId ? `${API}/forms/${editingId}` : `${API}/forms`;
      const method = editingId ? "PUT" : "POST";
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(draft),
      });
      if (res.ok) {
        toast.success(editingId ? "Form updated" : "Form created");
        setDraft(null); setEditingId(null); load();
      } else { toast.error("Save failed"); }
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (formId) => {
    if (!window.confirm("Delete this form? Its embed link will stop working.")) return;
    try {
      const res = await fetch(`${API}/forms/${formId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Form deleted"); load(); }
    } catch { toast.error("Delete failed"); }
  };

  const copy = async (text, label) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error("clipboard unavailable");
      }
      toast.success(`${label} copied`);
    } catch {
      // Fallback for iframes / restricted permissions policy
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(ok ? `${label} copied` : `Select the text and copy manually`);
      } catch {
        toast.error("Couldn't copy automatically — please select and copy manually");
      }
    }
  };
  const linkFor = (formId) => `${window.location.origin}/audit-form?form_id=${formId}`;
  const codeFor = (form) => {
    const id = `gmpulse-${form.form_id}`;
    const width = form.form_width || 520;
    const src = `${linkFor(form.form_id)}&embed=1`;
    // Tight per-layout fallback height: keeps the form fully visible (no clipping)
    // on site builders that strip the <script> (Wix/Squarespace/GoDaddy etc.) or
    // nest the embed, WITHOUT leaving large empty space. The script below still
    // fine-tunes to the exact content height on builders that allow it.
    const layout = form.layout || "centered";
    const fallbackH = layout === "split" ? 600 : layout === "compact" ? 500 : 540;
    return `<iframe id="${id}" src="${src}" title="Audit Form" scrolling="no" style="border:0;width:100%;max-width:${width}px;height:${fallbackH}px;display:block;margin:0 auto;background:transparent;"></iframe>
<script>
window.addEventListener("message",function(e){
  if(e.data&&e.data.type==="auditpro-resize"&&e.data.formId==="${form.form_id}"){
    var f=document.getElementById("${id}");if(f&&e.data.height){f.style.height=e.data.height+"px";}
  }
});
</script>`;
  };

  const togglePool = (id) => setDraft((d) => {
    const has = d.pool_user_ids.includes(id);
    return { ...d, pool_user_ids: has ? d.pool_user_ids.filter((x) => x !== id) : [...d.pool_user_ids, id] };
  });

  const updateRule = (i, patch) => setDraft((d) => {
    const rules = [...d.geo_rules];
    rules[i] = { ...rules[i], ...patch };
    return { ...d, geo_rules: rules };
  });
  const addRule = () => setDraft((d) => ({ ...d, geo_rules: [...d.geo_rules, { states: [], user_id: null }] }));
  const removeRule = (i) => setDraft((d) => ({ ...d, geo_rules: d.geo_rules.filter((_, x) => x !== i) }));

  if (loading) return <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="forms-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Lead Capture Forms</h3>
          <p className="text-xs text-muted-foreground">Create embeddable forms and choose how their leads are routed to your team.</p>
        </div>
        <Button data-testid="new-form-btn" onClick={openNew} className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Form
        </Button>
      </div>

      {forms.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200 shadow-none">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No forms yet. Create your first lead capture form.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {forms.map((f) => (
            <FormCard key={f.form_id} f={f} teamName={teamName} openEdit={openEdit} remove={remove}
              linkFor={linkFor} codeFor={codeFor} copy={copy} />
          ))}
        </div>
      )}

      <FormEditorDialog
        draft={draft} setDraft={setDraft} editingId={editingId} saving={saving} save={save} team={team}
        previewRef={previewRef} postPreview={postPreview}
        togglePool={togglePool} updateRule={updateRule} addRule={addRule} removeRule={removeRule}
        onClose={closeEditor} />
    </div>
  );
}
